/**
 * AudioService - Encapsula Web Speech API para STT e TTS
 * Singleton: todas as instâncias compartilham o mesmo estado
 */
class AudioService {
  constructor() {
    this.recognition = null
    this.isListening = false
    this._ptBrVoice = null
    this._voicesLoaded = false

    // Pré-carrega vozes pt-BR
    if (this.isSpeechSynthesisSupported()) {
      this._loadVoices()
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.addEventListener('voiceschanged', () => {
          this._loadVoices()
        })
      }
    }
  }

  // ========== Feature Detection ==========

  isSpeechRecognitionSupported() {
    if (typeof window === 'undefined') return false
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition)
  }

  isSpeechSynthesisSupported() {
    if (typeof window === 'undefined') return false
    return !!window.speechSynthesis
  }

  // ========== Speech-to-Text (STT) ==========

  startRecognition({ lang = 'pt-BR', onResult, onEnd, onError } = {}) {
    if (!this.isSpeechRecognitionSupported()) return

    // Para qualquer reconhecimento em andamento
    this.stopRecognition()

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    this.recognition = new SpeechRecognition()
    this.recognition.lang = lang
    this.recognition.continuous = false
    this.recognition.interimResults = true

    this.recognition.onresult = (event) => {
      let transcript = ''
      let isFinal = false

      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript
        if (event.results[i].isFinal) {
          isFinal = true
        }
      }

      if (onResult) onResult(transcript, isFinal)
    }

    this.recognition.onend = () => {
      this.isListening = false
      if (onEnd) onEnd()
    }

    this.recognition.onerror = (event) => {
      this.isListening = false
      if (onError) onError(event)
    }

    this.recognition.start()
    this.isListening = true
  }

  stopRecognition() {
    if (this.recognition && this.isListening) {
      this.recognition.stop()
      this.isListening = false
    }
  }

  abortRecognition() {
    if (this.recognition) {
      this.recognition.abort()
      this.isListening = false
    }
  }

  // ========== Text-to-Speech (TTS) ==========

  speak(text, { onEnd, onError, onStart } = {}) {
    if (!this.isSpeechSynthesisSupported()) return null

    // Cancela fala anterior
    window.speechSynthesis.cancel()

    const cleanedText = this._cleanTextForSpeech(text)
    if (!cleanedText.trim()) return null

    const utterance = new SpeechSynthesisUtterance(cleanedText)
    utterance.lang = 'pt-BR'
    utterance.rate = 1.0
    utterance.pitch = 1.0

    // Tenta usar voz pt-BR
    if (this._ptBrVoice) {
      utterance.voice = this._ptBrVoice
    }

    if (onStart) utterance.onstart = onStart
    if (onEnd) utterance.onend = onEnd
    if (onError) utterance.onerror = onError

    window.speechSynthesis.speak(utterance)
    return utterance
  }

  stop() {
    if (this.isSpeechSynthesisSupported()) {
      window.speechSynthesis.cancel()
    }
  }

  isSpeaking() {
    if (!this.isSpeechSynthesisSupported()) return false
    return window.speechSynthesis.speaking
  }

  // ========== Internals ==========

  _loadVoices() {
    if (!this.isSpeechSynthesisSupported()) return
    const voices = window.speechSynthesis.getVoices()
    this._ptBrVoice = voices.find(v => v.lang === 'pt-BR') ||
                      voices.find(v => v.lang.startsWith('pt')) ||
                      null
    this._voicesLoaded = voices.length > 0
  }

  _cleanTextForSpeech(text) {
    if (!text) return ''

    return text
      // Remove blocos de código (```)
      .replace(/```[\s\S]*?```/g, '')
      // Remove código inline (`)
      .replace(/`[^`]+`/g, '')
      // Converte links [text](url) → text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Remove marcadores de header (#)
      .replace(/^#{1,6}\s+/gm, '')
      // Remove bold/italic (** e *)
      .replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1')
      // Remove underline italic/bold
      .replace(/_{1,3}([^_]+)_{1,3}/g, '$1')
      // Remove separadores de tabela (|---|---|)
      .replace(/\|[-:\s]+\|[-:\s|]+/g, '')
      // Remove pipes de tabela
      .replace(/\|/g, ' ')
      // Remove traços horizontais (---)
      .replace(/^-{3,}$/gm, '')
      // Colapsa espaços múltiplos
      .replace(/\s+/g, ' ')
      // Remove espaços extras no início/fim
      .trim()
  }
}

export default new AudioService()
